import React, { useState } from "react";
import axios from "axios";
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";
import {
  Form,
  FormGroup,
  Label,
  Input,
  SubmitButton,
  ErrorMessage,
  SuccessMessage,
  PasswordWrapper,
  EyeIcon,
  ValidationList,
  ValidationItem,
} from "./Login.styles.js";

const passwordRequirements = [
  { id: "length", text: "At least 8 characters", regex: /.{8,}/ },
  { id: "uppercase", text: "At least one uppercase letter", regex: /[A-Z]/ },
  { id: "lowercase", text: "At least one lowercase letter", regex: /[a-z]/ },
  { id: "number", text: "At least one number", regex: /\d/ },
  {
    id: "special",
    text: "At least one special character",
    regex: /[!@#$%^&*(),.?":{}|<>]/,
  },
];

const RegisterForm = ({ switchToLogin }) => {
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirm: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const getPasswordStrength = (password) => {
    return passwordRequirements.map((req) => ({
      ...req,
      isValid: req.regex.test(password),
    }));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validation = getPasswordStrength(formData.password);
    const allValid = validation.every((req) => req.isValid);

    if (!allValid) {
      setError("Please meet all password requirements");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/auth/register`,
        {
          email: formData.email,
          username: formData.username,
          password: formData.password,
        }
      );

      setSuccess(response.data.message);

      // Clear form
      setFormData({
        email: "",
        username: "",
        password: "",
        confirmPassword: "",
      });

      // Switch to login after 2 seconds
      setTimeout(() => {
        switchToLogin();
        setSuccess("");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = getPasswordStrength(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword;

  return (
    <Form onSubmit={handleSubmit}>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <FormGroup>
        <Label htmlFor="register-email">Email Address</Label>
        <Input
          type="email"
          id="register-email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="Enter your email"
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="register-username">Username</Label>
        <Input
          type="text"
          id="register-username"
          name="username"
          value={formData.username}
          onChange={handleChange}
          required
          placeholder="Choose a username"
          minLength="3"
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="register-password">Password</Label>
        <PasswordWrapper>
          <Input
            type={showPassword.password ? "text" : "password"}
            id="register-password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            placeholder="Create a strong password"
            style={{ paddingRight: "40px" }}
          />
          <EyeIcon
            onClick={() =>
              setShowPassword((prev) => ({ ...prev, password: !prev.password }))
            }
          >
            {showPassword.password ? <FaEyeSlash /> : <FaEye />}
          </EyeIcon>
        </PasswordWrapper>

        {formData.password && (
          <ValidationList>
            {passwordValidation.map((req) => (
              <ValidationItem key={req.id} isValid={req.isValid}>
                {/* {req.isValid ? <FaCheck size={12} /> : <FaTimes size={12} />} */}
                {req.text}
              </ValidationItem>
            ))}
          </ValidationList>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="confirm-password">Confirm Password</Label>
        <PasswordWrapper>
          <Input
            type={showPassword.confirm ? "text" : "password"}
            id="confirm-password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            placeholder="Confirm your password"
            style={{ paddingRight: "40px" }}
          />
          <EyeIcon
            onClick={() =>
              setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))
            }
          >
            {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
          </EyeIcon>
        </PasswordWrapper>
        {formData.confirmPassword && (
          <ValidationItem isValid={passwordsMatch}>
            Passwords match
          </ValidationItem>
        )}
      </FormGroup>

      <SubmitButton type="submit" disabled={loading}>
        {loading ? "Creating account..." : "Sign Up"}
      </SubmitButton>
    </Form>
  );
};

export default RegisterForm;
